<template>
  <v-skeleton-loader :loading="showLoader" type="list-item-two-line" width="100%" style="background-color: transparent">
    <div class="note-composer">
      <div v-if="title" class="note-composer__title">{{ title }}</div>
      <div v-if="errorMsg" class="note-composer__error">{{ errorMsg }}</div>

      <div v-if="uploadedFile" class="note-composer__file">
        <v-icon size="16" color="accent">mdi-paperclip</v-icon>
        <span class="note-composer__file-name">{{ uploadedFile.fileName }}</span>
        <v-btn icon="mdi-close" variant="text" size="x-small" density="comfortable" @click="clearUploadedFile" />
      </div>
      <v-textarea
        v-else
        v-model="newNote"
        class="note-composer__input"
        placeholder="Add a note..."
        rows="2"
        auto-grow
        hide-details />

      <div class="note-composer__actions">
        <template v-if="!uploadedFile">
          <v-btn
            v-if="!showFileUpload"
            variant="text"
            size="small"
            color="primary"
            prepend-icon="mdi-paperclip"
            @click="showFileUpload = true">
            Attach a file
          </v-btn>
          <UploadFileComponent v-if="showFileUpload" @download-link="onDownloadLink" />
        </template>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          size="small"
          class="note-composer__submit"
          :disabled="(!newNote || newNote.trim() === '') && !uploadedFile"
          @click="addNote">
          {{ addNoteButtonLabel }}
        </v-btn>
      </div>
    </div>
  </v-skeleton-loader>
</template>

<script setup>
import { ref } from "vue";
import { userNotesService } from "@/services/userNotesService";
import UploadFileComponent from "@/components/UploadFileComponent.vue";

const showLoader = ref(false);
const newNote = ref("");
const errorMsg = ref("");
const showFileUpload = ref(false);
const uploadedFile = ref(null);

const onDownloadLink = (download) => {
  uploadedFile.value = { link: download.link, fileName: download.fileName };
  showFileUpload.value = false;
};

const clearUploadedFile = () => {
  uploadedFile.value = null;
};

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  orderInfoId: {
    type: String,
    required: false,
    default: null,
  },
  intakeOrderId: {
    type: Number,
    required: false,
    default: null,
  },
  intakeDocumentId: {
    type: String,
    required: false,
    default: null,
  },
  onAddNote: {
    type: [Function, null],
    required: false,
    default: null,
  },
  overrideAddNote: {
    type: [Function, null],
    required: false,
    default: null,
  },
  addNoteButtonLabel: {
    type: String,
    required: false,
    default: "Add Note",
  },
});

const addNote = async () => {
  try {
    let noteContent;

    if (uploadedFile.value) {
      // Store file as: [FILE:filename](url)
      noteContent = `[FILE:${uploadedFile.value.fileName}](${uploadedFile.value.link})`;
    } else {
      noteContent = newNote.value.trim();
    }

    if (!noteContent) return;

    if (props.overrideAddNote) {
      await props.overrideAddNote(noteContent, {
        orderInfoId: props.orderInfoId,
        intakeOrderId: props.intakeOrderId,
        intakeDocumentId: props.intakeDocumentId,
      });
      newNote.value = "";
      uploadedFile.value = null;
      return;
    }
    errorMsg.value = "";
    showLoader.value = true;
    const createdNote = await userNotesService.createNote({
      orderInfo: props.orderInfoId,
      intakeOrder: props.intakeOrderId,
      intakeDocument: props.intakeDocumentId,
      note: noteContent,
    });
    newNote.value = "";
    uploadedFile.value = null;
    if (props.onAddNote) {
      props.onAddNote(createdNote);
    }
  } catch (e) {
    errorMsg.value = `Failed to add note: ${e.message}`;
  } finally {
    showLoader.value = false;
  }
};
</script>

<style lang="scss" scoped>
.note-composer {
  font-family: $lh_font_family_dm_sans;
  width: 100%;
}

.note-composer__title {
  font-size: 15px;
  font-weight: 600;
  color: $lh_color_navy_dark;
  margin-bottom: 10px;
}

.note-composer__error {
  color: $lh_color_red;
  font-size: 13.5px;
  margin-bottom: 8px;
}

.note-composer__input {
  :deep(.v-field) {
    background: $lh_color_snowy_white;
    border: 1px solid $lh_dash_border;
    border-radius: 12px;
    box-shadow: none;
    font-size: 14px;
    transition: border-color $lh_transition_fast, background $lh_transition_fast, box-shadow $lh_transition_fast;

    textarea {
      font-family: $lh_font_family_dm_sans;
      color: $lh_color_navy;
    }

    &.v-field--focused {
      background: $lh_dash_surface;
      border-color: rgba($lh_color_blue, 0.3);
      box-shadow: 0 0 0 3px rgba($lh_color_blue, 0.08);
    }
  }
}

.note-composer__file {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba($lh_color_blue, 0.05);
  border: 1px solid rgba($lh_color_blue, 0.2);
  border-radius: 12px;

  .note-composer__file-name {
    font-size: 13.5px;
    font-weight: 500;
    color: $lh_color_navy;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1 1 auto;
  }
}

.note-composer__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.note-composer__submit {
  min-width: 96px;
}
</style>
